/**
 * Named custom output recipes — multi-step sequences users build and reuse.
 */

import type { ActionThen } from "../engines/types";

const KEY = "visionforge.customOutputs.v1";

export type CustomOutput = {
  id: string;
  name: string;
  steps: ActionThen[];
  fireWhenPaused?: boolean;
  updatedAt: number;
};

export function loadCustomOutputs(): CustomOutput[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomOutput[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((o) => o && o.id && o.name && Array.isArray(o.steps));
  } catch {
    return [];
  }
}

export function saveCustomOutputs(list: CustomOutput[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function getCustomOutput(id: string): CustomOutput | undefined {
  return loadCustomOutputs().find((o) => o.id === id);
}

export function upsertCustomOutput(output: CustomOutput): CustomOutput[] {
  const list = loadCustomOutputs();
  const i = list.findIndex((o) => o.id === output.id);
  const next = [...list];
  if (i >= 0) next[i] = output;
  else next.unshift(output);
  saveCustomOutputs(next);
  return next;
}

export function deleteCustomOutput(id: string): CustomOutput[] {
  const next = loadCustomOutputs().filter((o) => o.id !== id);
  saveCustomOutputs(next);
  return next;
}

export function newCustomOutputId(): string {
  return `co-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function defaultStep(): ActionThen {
  return { kind: "toast", message: "{{gesture_label}} fired" };
}
